import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

// /v1/platform/tenants returns T001Tenant rows flat; there is no nested profile.
interface TenantRow {
  readonly tenantId: string;
  readonly tenantCode: string;
  readonly tenantName: string;
  readonly status: string;
  readonly userCount: number;
  readonly companyCount: number;
}

export function M19TenantManagementPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<TenantRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const [tenantCode, setTenantCode] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [region, setRegion] = useState('');
  const [plan, setPlan] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  // BR-TEN-03: the tenant and its primary company are created together, so the
  // company fields are part of this form, not a later step.
  const [companyCode, setCompanyCode] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [country, setCountry] = useState('IN');
  const [panNumber, setPanNumber] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ data: TenantRow[] }>('/v1/platform/tenants', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const transition = async (code: string, action: 'activate' | 'suspend' | 'archive'): Promise<void> => {
    setBusyKey(code + action);
    try {
      await apiRequest(`/v1/platform/tenants/${code}/${action}`, { method: 'POST', token });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      await apiRequest('/v1/platform/tenants', {
        method: 'POST', token,
        body: {
          tenantCode,
          tenantName,
          region: region || undefined,
          plan: plan || undefined,
          contactEmail: contactEmail || undefined,
          companyCode,
          companyName,
          legalName,
          country: country || undefined,
          panNumber: panNumber || undefined,
        },
      });
      setCreated(true);
      setTenantCode('');
      setTenantName('');
      setRegion('');
      setPlan('');
      setContactEmail('');
      setCompanyCode('');
      setCompanyName('');
      setLegalName('');
      setPanNumber('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{tr('m19.tenant.loading')}</p>;
  return (
    <main aria-label="tenant-management">
      <h1>{tr('m19.tenant.title')}</h1>

      <form onSubmit={onCreate} aria-label="tenant-create-form">
        <h2>{tr('m19.tenant.newTenant')}</h2>
        <label htmlFor="tn-code">{tr('m19.tenant.labelCode')}</label>
        <input id="tn-code" value={tenantCode} onChange={(e) => setTenantCode(e.target.value)} required />
        <label htmlFor="tn-name">{tr('m19.tenant.labelName')}</label>
        <input id="tn-name" value={tenantName} onChange={(e) => setTenantName(e.target.value)} required />
        <label htmlFor="tn-region">{tr('m19.tenant.labelRegion')}</label>
        <input id="tn-region" value={region} onChange={(e) => setRegion(e.target.value)} />
        <label htmlFor="tn-plan">{tr('m19.tenant.labelPlan')}</label>
        <input id="tn-plan" value={plan} onChange={(e) => setPlan(e.target.value)} />
        <label htmlFor="tn-email">{tr('m19.tenant.labelEmail')}</label>
        <input id="tn-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        <label htmlFor="tn-company-code">{tr('m19.tenant.labelCompanyCode')}</label>
        <input id="tn-company-code" value={companyCode} onChange={(e) => setCompanyCode(e.target.value)} required />
        <label htmlFor="tn-company-name">{tr('m19.tenant.labelCompanyName')}</label>
        <input id="tn-company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
        <label htmlFor="tn-legal-name">{tr('m19.tenant.labelLegalName')}</label>
        <input id="tn-legal-name" value={legalName} onChange={(e) => setLegalName(e.target.value)} required />
        <label htmlFor="tn-country">{tr('m19.tenant.labelCountry')}</label>
        <input id="tn-country" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} maxLength={2} />
        {/* No GSTIN at tenant creation: a company registers for GST per state,
            so registrations are added later on the company GST screen. */}
        <label htmlFor="tn-pan">{tr('m19.tenant.labelPan')}</label>
        <input id="tn-pan" value={panNumber} onChange={(e) => setPanNumber(e.target.value.toUpperCase())} required={country === 'IN'} />
        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{tr('m19.tenant.created')}</p> : null}
        <button type="submit" disabled={creating}>{creating ? tr('m19.tenant.creating') : tr('m19.tenant.create')}</button>
      </form>

      <table aria-label="tenants-table">
        <thead><tr><th>{tr('m19.tenant.colCode')}</th><th>{tr('m19.tenant.colName')}</th><th>{tr('m19.tenant.colStatus')}</th><th>{tr('m19.tenant.colUsers')}</th><th>{tr('m19.tenant.colCompanies')}</th><th>{tr('m19.tenant.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.tenantId}>
              <td>{t.tenantCode}</td>
              <td>{t.tenantName}</td>
              <td>{t.status}</td>
              <td aria-label={`user-count-${t.tenantCode}`}>{t.userCount ?? 0}</td>
              <td aria-label={`company-count-${t.tenantCode}`}>{t.companyCount ?? 0}</td>
              <td>
                <button type="button" aria-label={`activate-${t.tenantCode}`} disabled={busyKey === t.tenantCode + 'activate'} onClick={() => void transition(t.tenantCode, 'activate')}>{tr('m19.tenant.activate')}</button>
                <button type="button" aria-label={`suspend-${t.tenantCode}`} disabled={busyKey === t.tenantCode + 'suspend'} onClick={() => void transition(t.tenantCode, 'suspend')}>{tr('m19.tenant.suspend')}</button>
                <button type="button" aria-label={`archive-${t.tenantCode}`} disabled={busyKey === t.tenantCode + 'archive'} onClick={() => void transition(t.tenantCode, 'archive')}>{tr('m19.tenant.archive')}</button>
                <Link to={`/admin/platform/tenants/${t.tenantCode}/edit`} aria-label={`edit-${t.tenantCode}`}>{tr('m19.tenant.edit')}</Link>
                <Link to={`/admin/platform/tenants/${t.tenantCode}/licence`} aria-label={`licence-${t.tenantCode}`}>{tr('m19.tenant.licence')}</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
